/**
 * THE MONEY, IN THE ORDER IT IS SPENT (owner, 22 Sep 2026: "it just grouped the savings as a lump
 * sum … this should be client specific"; and on the rail: "specifically call out when the bank
 * account runs out before moving to the next asset").
 *
 * Each pool the client holds, named as they named it, and the projected year it runs low; then the
 * year everything is gone. Read off the projection rows the runway is struck on, so the years agree
 * with the headline above them. Two shapes from one derivation: a list for step 2 (`buildMoneySequence`)
 * and one brief sentence for the plan summary rail (`moneySequenceSentence`), so the two never tell
 * different stories.
 */
import React from 'react';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/** A projection row as the runway reads it: balances by pool, per year. */
export interface RunwayRow {
    year?: number;
    assets?: { bankAccounts?: Array<{ value?: unknown }>; accountBasedPensions?: Array<{ value?: unknown }>; shares?: Array<{ value?: unknown }> };
}

type PoolKey = 'bankAccounts' | 'accountBasedPensions' | 'shares';
interface Pool {
    key: PoolKey;
    /** "the everyday accounts" — the noun phrase, lower case, with its article. */
    plural: string;
    singularVerb: boolean;
    today: number;
    names: string;
    /** The projected year the pool falls below $1,000; null when it never does within the rows. */
    year: number | null;
}

const sumPool = (list: Array<{ value?: unknown }> | undefined): number =>
    (list || []).reduce((t, a) => t + (parseFloat(String(a?.value ?? 0)) || 0), 0);

function poolsOf(rows: RunwayRow[], factFind: DraftFactFind): Pool[] {
    const assets = (factFind.assets || {}) as Record<PoolKey, Array<{ description?: string; value?: unknown }> | undefined>;
    const make = (key: PoolKey, plural: string, singularVerb: boolean): Pool => {
        const list = assets[key];
        const row = rows.find((r) => sumPool(r.assets?.[key]) < 1000);
        return {
            key,
            plural,
            singularVerb,
            today: sumPool(list),
            names: (list || []).map((a) => a.description).filter(Boolean).join(', '),
            year: row ? Number(row.year) || rows.indexOf(row) + 1 : null,
        };
    };
    return [
        make('bankAccounts', 'the everyday accounts', false),
        make('accountBasedPensions', 'the account-based pension', true),
        make('shares', 'the shares', false),
    ]
        .filter((p) => p.today > 1000)
        // In the order the engine empties them; a pool that never empties sits last.
        .sort((a, b) => (a.year ?? 1e9) - (b.year ?? 1e9));
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const withNames = (p: Pool) => `${p.plural} (${p.names ? `${p.names}, ` : ''}${formatCurrency(Math.round(p.today))} today)`;

/** Step 2's list: one line per hand-over, with the year it happens. */
export function buildMoneySequence(rows: RunwayRow[], factFind: DraftFactFind, runway: PlanRunwayCopy | null): Array<{ from: string; text: React.ReactNode }> {
    if (!rows.length) return [];
    const pools = poolsOf(rows, factFind);
    const out: Array<{ from: string; text: React.ReactNode }> = [];
    pools.forEach((p, i) => {
        const next = pools[i + 1];
        if (i === 0) {
            out.push({ from: 'From year 1', text: <>The gap comes out of <span className="font-semibold text-slate-900">{withNames(p)}</span>.</> });
        }
        // The last pool running low IS the savings running out: one line, not two, for that year.
        const isTheEnd = !next && runway?.exhaustedYear != null && p.year === runway.exhaustedYear;
        if (p.year != null && !isTheEnd) {
            out.push({
                from: `Year ${p.year}`,
                text: next ? (
                    <>
                        <span className="font-semibold text-slate-900">{cap(p.plural)}</span> run{p.singularVerb ? 's' : ''} low;{' '}
                        <span className="font-semibold text-slate-900">{withNames(next)}</span> carr{next.singularVerb ? 'ies' : 'y'} it from here.
                    </>
                ) : (
                    <>
                        <span className="font-semibold text-slate-900">{cap(p.plural)}</span> run{p.singularVerb ? 's' : ''} low.
                    </>
                ),
            });
        }
    });
    if (runway?.radDrawStartsYear != null) {
        out.push({
            from: `Year ${runway.radDrawStartsYear}`,
            text: <>The care fees are drawn from the refundable accommodation deposit, which reduces the refund when care ends and raises the daily accommodation payment.</>,
        });
    }
    if (runway?.exhaustedYear != null) {
        out.push({
            from: `Year ${runway.exhaustedYear}`,
            text: runway.borrowingStartsYear != null
                ? <>The savings are gone. From here the plan borrows against the home — nothing is repaid (assumes a reverse mortgage), so the debt grows until the home is sold, and it reduces what is left to the family.</>
                : <>The savings are gone, and there is nothing accessible left to cover the shortfall — worth planning another source.</>,
        });
    } else if (runway && !runway.depleted) {
        out.push({ from: 'Beyond', text: <>The savings are not exhausted within the years tested.</> });
    }
    return out;
}

/**
 * The horizon line, and what the horizon is FOR.
 *
 * Owner, 22 Sep 2026: an adviser reading "year 17 … borrows against the home" for an 87-year-old
 * will say "not relevant, the client won't live that long" — and dismiss the runway with it. The
 * 25 years are a test of how far the money stretches (so a depletion in year 13 is never hidden by
 * a five-year window), not a forecast of the person. This says so in every case, and names
 * borrowing against the home only when the projection actually borrows; otherwise the later years
 * are just later years. No life expectancy is quoted — that would need a source and a date, and
 * would invite an argument about the number instead of the point.
 */
export const HORIZON_CLOSE = 'The early years are the ones to plan for.';

export function horizonNoteLead(runway: PlanRunwayCopy): string {
    const home = runway.borrowingStartsYear != null ? ', including any borrowing against the home,' : '';
    return `${runway.basis} This shows how far the money would stretch, not how long care will last. Many stays are much shorter, so the later years${home} may never arrive.`;
}

/** The whole note as one string — for tests and any surface that cannot render markup. */
export function horizonNote(runway: PlanRunwayCopy): string {
    return `${horizonNoteLead(runway)} ${HORIZON_CLOSE}`;
}

/**
 * The note as it is read: the explanation quiet, the one line that must land set darker and
 * semibold (owner, 22 Sep 2026: "it all looks very grey … nothing too bold"). No colour — blue
 * reads as a link and green or amber as a status, and this is neither. `className` caps the width
 * where the note would otherwise run the full page.
 */
export function HorizonNote({ runway, className = '' }: { runway: PlanRunwayCopy; className?: string }) {
    return (
        <p className={`text-[13px] leading-relaxed text-slate-500 ${className}`}>
            {horizonNoteLead(runway)} <span className="font-semibold text-slate-700">{HORIZON_CLOSE}</span>
        </p>
    );
}

/**
 * The rail's one sentence: which account runs low when, what takes over, and when the savings are
 * gone. The reverse mortgage is the last clause, not the headline — at most clients' ages it is the
 * least likely part of the story (owner, 22 Sep 2026).
 */
export function moneySequenceSentence(rows: RunwayRow[], factFind: DraftFactFind, runway: PlanRunwayCopy | null): string | null {
    if (!rows.length || !runway) return null;
    const pools = poolsOf(rows, factFind);
    if (!pools.length) return null;
    const clauses: string[] = [];
    pools.forEach((p, i) => {
        const next = pools[i + 1];
        const isTheEnd = !next && runway.exhaustedYear != null && p.year === runway.exhaustedYear;
        if (p.year == null || isTheEnd) return;
        clauses.push(`${cap(p.plural)} run${p.singularVerb ? 's' : ''} low around year ${p.year}${next ? `, then ${next.plural} carr${next.singularVerb ? 'ies' : 'y'} it` : ''}`);
    });
    if (runway.radDrawStartsYear != null) {
        clauses.push(`from about year ${runway.radDrawStartsYear} the care fees are drawn from the refundable accommodation deposit, which reduces the refund at the end and raises the daily accommodation payment`);
    }
    if (runway.exhaustedYear != null) {
        clauses.push(
            runway.borrowingStartsYear != null
                ? `the savings are gone around year ${runway.exhaustedYear}, and from there the plan borrows against the home (assumes a reverse mortgage) — a debt that grows until the home is sold`
                : `the savings are gone around year ${runway.exhaustedYear}, and there is nothing accessible left to cover the shortfall`,
        );
    } else if (!runway.depleted) {
        clauses.push('the savings are not exhausted within the years tested');
    }
    if (!clauses.length) return null;
    return clauses.map((c, i) => (i === 0 ? c : c.charAt(0).toLowerCase() + c.slice(1))).join('; ') + '.';
}

/**
 * "Where the shortfall comes from" for Explain this (owner, 15 Oct 2026, option A): the order the
 * pools are used with the year each hand-over happens, the plain fact that the later steps are
 * rarely reached, and the deposit's refund — which is where the client's mind already is. No test
 * length is quoted: the years tested are a check of how far the money stretches, not a forecast of
 * the person (see HorizonNote), and "assumes care continues for 25 years" invites exactly the
 * objection the owner made. Null where there is no shortfall or no pool to read.
 */
export function moneyOrderParagraph(
    rows: RunwayRow[],
    factFind: DraftFactFind,
    runway: PlanRunwayCopy | null,
    deposit: { refund: number; refundTo: string },
): string | null {
    if (!rows.length || !runway || !runway.depleted && !runway.bankDepletesYear && runway.coveredPct === 100 && !runway.flag && runway.headline.startsWith('Cashflow stays positive')) return null;
    const pools = poolsOf(rows, factFind);
    if (!pools.length) return null;
    const steps = pools.map((p, i) => {
        const prev = pools[i - 1];
        if (i === 0) return p.plural;
        return `then ${p.plural}${prev?.year != null ? ` from about year ${prev.year}` : ''}`;
    });
    const order = steps.length === 1
        ? `The shortfall comes out of ${steps[0]}.`
        : `The shortfall is met in this order: ${steps.join(', ')}.`;
    const later = steps.length > 1 ? ' In practice care usually ends well before the later steps are reached' : ' In practice care usually ends well before the savings are';
    const refund = deposit.refund > 0
        ? `${later} — and when it does, the ${formatCurrency(Math.round(deposit.refund))} deposit comes back to ${deposit.refundTo}, which is why it sits in the estate figure.`
        : `${later}${steps.length > 1 ? '.' : ' gone.'}`;
    return `${order}${refund}`;
}
